/**
 * categoryPreference (HOF-106) — /documents' last selected lens (`?category=`,
 * the taxonomy-v2 *domain*), remembered per tenant.
 *
 * The domain lens is this page's primary selection — it decides which slice of
 * the archive the feed renders. `?sub=` (the role), `?doc=` and the sort/view
 * axes are out of scope, the same discipline HOF-101 applied to
 * `?g=`/`?py=`/`?view=`.
 *
 * Two things are peculiar to this page:
 *
 *  - **`all` is a sentinel, not a domain.** „Alle Dokumente" *is* the absence of
 *    a `?category=` filter, which is why `handleCategorySelect` deletes the
 *    param for it. It is nevertheless a real, explicit selection and is stored
 *    as such — a reader who deliberately returns to the unfiltered view must not
 *    be thrown back into their old lens on the next visit. It is therefore
 *    always valid, and the page restores it by stamping *nothing*.
 *  - **the lens list is count-derived.** `buildCategories` emits only domains
 *    with `count > 0`, so a domain whose last document was deleted or
 *    reclassified simply stops being a lens. A stored id has to be checked
 *    against that list, because the page has no fallback for an unknown
 *    `?category=`: it forwards it as a `domain=` filter and renders an empty
 *    feed under a breadcrumb that cannot name it.
 *
 * The URL parameter is deliberately NOT put through this rule. An explicit
 * `?category=` is a statement about this visit — and #2009's deep-link jump
 * writes one for the domain of the document being opened — so validating it
 * here would change deep-link behaviour, which is a different issue from
 * remembering a selection.
 */
import { createLensPreference } from '../../utils/lensPreference';
import { ALL_DOCUMENTS_LENS_ID } from './categoryConfig';

/** Key prefix; the tenant id is appended. */
export const DOCUMENTS_CATEGORY_PREFERENCE_PREFIX = 'hofsync.documents.category';

const preference = createLensPreference(DOCUMENTS_CATEGORY_PREFERENCE_PREFIX);

export const documentsCategoryPreferenceKey = preference.key;
export const readStoredCategory = preference.read;
export const writeStoredCategory = preference.write;

/**
 * The remembered lens, but only if the nav still offers it. The all-documents
 * sentinel is always offered — it needs no counts to be reachable.
 */
export function resolveStoredCategory(
  stored: string | null,
  lenses: readonly { id: string }[],
): string | null {
  if (!stored) return null;
  if (stored === ALL_DOCUMENTS_LENS_ID) return ALL_DOCUMENTS_LENS_ID;
  return lenses.some((c) => c.id === stored) ? stored : null;
}
